import {
  BasePageModel,
  basePageToLocal,
  DiffStates,
  Reference,
  TrainingPhraseConfig,
  trainingPhraseToLocal,
  ReferenceNode,
  OriginKey,
  originKeyToLocal,
  referenceNodeToLocal,
  referenceNodeToServer,
  InputParameter,
  inputParameterToLocal,
  inputParameterToServer,
  AugmentMeta,
  augmentMetaToLocal,
  augmentMetaToServer
} from './index';
import { Operation, RESPONSE_CHANNEL_TYPE, SourceType } from '@config/index';

export interface ResponseUpdateFuncArgumentConfig {
  nextResponses?: ResponseConfig[];
  nextPrivateDelResponses?: ResponseConfig[];
  nextDelResponseID?: string;
}

export interface ConditionContent {
  kind: 'content';
  id?: string;
  operation: Operation;
  value: any[];
  operationAt?: string;
}

export interface TriggerCondition {
  kind: 'condition';
  variable: Reference;
  parameters: InputParameter[];
  conditions: ConditionContent[];
}

export const ActionType = {
  ITEM: 'item_action',
  LIST: 'list_action',
  MEDIA: 'media_action',
  INTENT: 'intent_action',
  INTENT_START: 'intent_start_action',
  EXTERNAL: 'external_action',
  INTENT_ABORT: 'intent_abort_action',
  HAND_OFF: 'hand_off_action',
  CLOSE_SESSION: 'close_session_action',
  CLEAR_SLOT: 'clear_slot_action',
  FILL_SLOT: 'fill_slot_action',
  RECHECK: 'recheck_action',
  INTENT_END: 'intent_end_action',
  AUGMENT_META: 'augment_meta'
};


export type ItemActionChannelData = {
  channel: string;
  content: string[];
};

export type ItemAction = {
  type: 'item_action';
  content: string[];
  templateId: string;
  otherChannelData?: ItemActionChannelData[];
};

export type ListActionChannelData = {
  channel: string;
  header: string;
  body: string;
  footer: string;
};

export type ListAction = {
  type: 'list_action';
  itemSize: number;
  header: string;
  footer: string;
  body: string;
  templateId: string;
  sourceType: SourceType;
  source: string;
  sourceComment?: string;
  variable: Reference;
  parameters: InputParameter[];
  delimiterField?: string;
  delimiterEnd?: string;
  singlePrompts?: string[];
  zeroPrompts?: string[];
  otherChannelData?: ListActionChannelData[];
};

export type MediaAction = {
  type: 'media_action';
  url: string;
  description: string;
};

export type IntentAssignment = {
  slot: Reference;
  codeExpression: string;
  codeComment?: string;
};

export type IntentStartAction = {
  type: 'intent_start_action';
  intent: ReferenceNode;
  assignments: IntentAssignment[];
  gated?: boolean;
};

export type ExternalAction = {
  type: 'external_action';
  name?: string;
  parameterType?: string;
  parameters: string[];
};

export type IntentAbortAction = {
  type: 'intent_abort_action';
  intent: ReferenceNode;
};

export type HandOffAction = {
  type: 'hand_off_action';
  department: ReferenceNode;
};

export type CloseSessionAction = {
  type: 'close_session_action';
};

export type ClearSlotAction = {
  type: 'clear_slot_action';
  slot: Reference;
};

export type FillSlotAction = {
  type: 'fill_slot_action';
  slot: Reference;
  codeExpression: string;
  codeComment?: string;
};

export type RecheckAction = {
  type: 'recheck_action';
  slot: Reference;
};

export type IntentEndAction = {
  type: 'intent_end_action';
  soft: boolean;
};

export enum ParamAction {
  REPLACE = 'replace',
  APPEND = 'append'
}

export type UnknownActoin = {
  type: 'unknown_action'
};

export type FramelyAction = (ItemAction
  | ListAction
  | MediaAction
  | IntentStartAction
  | ExternalAction
  | IntentAbortAction
  | HandOffAction
  | CloseSessionAction
  | ClearSlotAction
  | FillSlotAction
  | RecheckAction
  | IntentEndAction
  | AugmentMeta
  | UnknownActoin)
  & {
    id?: string;
  };

export interface ActionChannel {
  tag: RESPONSE_CHANNEL_TYPE;
  actions: FramelyAction[];
}

export interface ResponseConfig extends BasePageModel {
  conditionType: SourceType;
  triggerConditions: TriggerCondition[];
  codeCondition: string;
  codeComment: string;
  needConfirmation: boolean;
  confirmQuestions?: string[];
  confirmExpressionsYes?: TrainingPhraseConfig[];
  confirmExpressionsNo?: TrainingPhraseConfig[];
  channels: ActionChannel[];
  temp?: boolean;
  isElseBranch?: boolean;
  masterSelf?: ResponseConfig;
}

export function conditionToLocal(data: any): TriggerCondition {

  return {
    kind: 'condition',
    variable: (data.variable || []).map(referenceNodeToLocal),
    parameters: (data.parameters || []).map(inputParameterToLocal),
    conditions: (data.conditions || []).map((item: any) => ({
      kind: 'content',
      id: item.id,
      operationAt: item.operation_at,
      operation: item.operation,
      value: item.value
    }))
  };
}

export function conditionToServer(data: TriggerCondition) {

  return {
    variable: (data.variable || []).map(referenceNodeToServer),
    parameters: (data.parameters || []).map(inputParameterToServer),
    conditions: (data.conditions || []).map((item: ConditionContent) => ({
      id: item.id,
      operation_at: item.operationAt || '',
      operation: item.operation,
      value: item.value
    }))
  };
}

export function actionToLocal(data: any): FramelyAction {
  if (data.type === ActionType.INTENT) {
    data.type = ActionType.INTENT_START;
  }

  const resItem = data[data.type] || {};
  switch (data.type) {
    case ActionType.ITEM:
      return {
        id: data.id,
        type: 'item_action',
        content: resItem.content,
        templateId: resItem.template_id,
        otherChannelData: resItem.other_channel_data && resItem.other_channel_data.map((item: any) => ({
          channel: item.channel,
          content: item.content
        }))
      };
    case ActionType.LIST:
      return {
        id: data.id,
        type: 'list_action',
        itemSize: resItem.item_size,
        header: resItem.header,
        footer: resItem.footer,
        body: resItem.body,
        templateId: resItem.template_id,
        sourceType: resItem.source_type ||
          (resItem.source ? SourceType.CODE_EXPRESSION : SourceType.REGULAR),
        source: resItem.source,
        sourceComment: resItem.source_comment,
        variable: (resItem.variable || []).map(referenceNodeToLocal),
        parameters: (resItem.parameters || []).map(inputParameterToLocal),
        delimiterField: resItem.delimiter_field,
        delimiterEnd: resItem.delimiter_end,
        singlePrompts: resItem.single_prompts,
        zeroPrompts: resItem.zero_prompts,
        otherChannelData: resItem.other_channel_data && resItem.other_channel_data.map((item: any) => ({
          channel: item.channel,
          header: item.header,
          body: item.body,
          footer: item.footer
        }))
      };
    case ActionType.INTENT:
    case ActionType.INTENT_START:
      return {
        id: data.id,
        type: 'intent_start_action',
        intent: resItem.intent && {
          id: resItem.intent.id,
          name: resItem.intent.name,
          type: resItem.intent.type
        },
        assignments: (resItem.assignments || []).map((asm: any) => ({
          slot: asm.slot && asm.slot.map(referenceNodeToLocal),
          codeExpression: asm.code_expression,
          codeComment: asm.code_comment
        })),
        gated: resItem.gated
      };
    case ActionType.EXTERNAL:
      return {
        id: data.id,
        type: 'external_action',
        name: resItem.name,
        parameterType: resItem.parameter_type,
        parameters: resItem.parameters || []
      };
    case ActionType.INTENT_ABORT:
      return {
        id: data.id,
        type: 'intent_abort_action',
        intent: resItem.intent && {
          id: resItem.intent.id,
          name: resItem.intent.name,
          type: resItem.intent.type
        }
      };
    case ActionType.HAND_OFF:
      return {
        id: data.id,
        type: 'hand_off_action',
        department: resItem.department && {
          id: resItem.department.id,
          name: resItem.department.name,
          type: resItem.department.type
        }
      };
    case ActionType.CLOSE_SESSION:
      return {
        id: data.id,
        type: 'close_session_action'
      };
    case ActionType.CLEAR_SLOT:
      return {
        id: data.id,
        type: 'clear_slot_action',
        slot: resItem.slot && resItem.slot.map(referenceNodeToLocal)
      };
    case ActionType.FILL_SLOT:
      return {
        id: data.id,
        type: 'fill_slot_action',
        slot: resItem.slot && resItem.slot.map(referenceNodeToLocal),
        codeExpression: resItem.code_expression,
        codeComment: resItem.code_comment
      };
    case ActionType.RECHECK:
      return {
        id: data.id,
        type: 'recheck_action',
        slot: resItem.slot && resItem.slot.map(referenceNodeToLocal)
      };
    case ActionType.INTENT_END:
      return {
        id: data.id,
        type: 'intent_end_action',
        soft: resItem && resItem.soft
      };
    case ActionType.AUGMENT_META:
      return {
        id: data.id,
        type: 'augment_meta',
        ...resItem && augmentMetaToLocal(resItem)
      };
    default:
      return {
        type: 'unknown_action'
      };
  }
}

export function actionToServer(action: FramelyAction) {

  const ret: any = {};
  for (const v of Object.values(ActionType)) {
    ret[v as string] = null;
  }

  return {
    ...ret,
    ...actionToServerCore(action)
  };
}

export function actionToServerCore(action: FramelyAction) {
  switch (action.type) {
    case 'item_action':
      return {
        id: action.id,
        type: ActionType.ITEM,
        [ActionType.ITEM]: {
          template_id: action.templateId,
          content: (!action.content || !action.content.length)
            ? ['']
            : action.content,
          other_channel_data: action.otherChannelData && action.otherChannelData.map((item: any) => ({
            channel: item.channel,
            content: item.content
          }))
        },
        [ActionType.CLEAR_SLOT]: null
      };
    case 'list_action':
      return {
        id: action.id,
        type: ActionType.LIST,
        [ActionType.LIST]: {
          item_size: action.itemSize,
          header: action.header,
          footer: action.footer,
          body: action.body,
          template_id: action.templateId,
          source_type: action.sourceType,
          source: action.source,
          source_comment: action.sourceComment,
          variable: (action.variable || []).map(referenceNodeToServer),
          parameters: (action.parameters || []).map(inputParameterToServer),
          delimiter_field: action.delimiterField,
          delimiter_end: action.delimiterEnd,
          single_prompts: action.singlePrompts,
          zero_prompts: action.zeroPrompts,
          other_channel_data: action.otherChannelData && action.otherChannelData.map((item: any) => ({
            channel: item.channel,
            header: item.header,
            body: item.body,
            footer: item.footer
          }))
        }
      };
    case 'media_action':
      return {
        id: action.id,
        type: ActionType.MEDIA,
        [ActionType.MEDIA]: {
          url: action.url,
          description: action.description
        }
      };
    case 'intent_start_action':
      return {
        id: action.id,
        type: ActionType.INTENT_START,
        [ActionType.INTENT_START]: {
          intent: action.intent && {
            id: action.intent.id,
            name: action.intent.name,
            type: action.intent.type
          },
          assignments: (action.assignments || []).map((asm: IntentAssignment) => ({
            slot: asm.slot && asm.slot.map(referenceNodeToServer),
            code_expression: asm.codeExpression,
            code_comment: asm.codeComment
          })),
          gated: action.gated
        }
      };
    case 'external_action':
      return {
        id: action.id,
        type: ActionType.EXTERNAL,
        [ActionType.EXTERNAL]: {
          name: action.name,
          parameter_type: action.parameterType,
          parameters: action.parameters
        }
      };
    case 'intent_abort_action':
      return {
        id: action.id,
        type: ActionType.INTENT_ABORT,
        [ActionType.INTENT_ABORT]: {
          intent: action.intent && {
            id: action.intent.id,
            name: action.intent.name,
            type: action.intent.type
          }
        }
      };
    case 'hand_off_action':
      return {
        id: action.id,
        type: ActionType.HAND_OFF,
        [ActionType.HAND_OFF]: {
          department: referenceNodeToServer(action.department)
        }
      };
    case 'close_session_action':
      return {
        id: action.id,
        type: ActionType.CLOSE_SESSION
      };
    case 'clear_slot_action':
      return {
        id: action.id,
        type: ActionType.CLEAR_SLOT,
        [ActionType.CLEAR_SLOT]: {
          slot: action.slot && action.slot.map(referenceNodeToServer)
        }
      };
    case 'fill_slot_action':
      return {
        id: action.id,
        type: ActionType.FILL_SLOT,
        [ActionType.FILL_SLOT]: {
          slot: action.slot && action.slot.map(referenceNodeToServer),
          code_expression: action.codeExpression,
          code_comment: action.codeComment
        }
      };
    case 'recheck_action':
      return {
        id: action.id,
        type: ActionType.RECHECK,
        [ActionType.RECHECK]: {
          slot: action.slot && action.slot.map(referenceNodeToServer)
        }
      };
    case 'intent_end_action':
      return {
        id: action.id,
        type: ActionType.INTENT_END,
        [ActionType.INTENT_END]: {
          soft: action && action.soft
        }
      };
    case 'augment_meta':
      return {
        id: action.id,
        type: ActionType.AUGMENT_META,
        [ActionType.AUGMENT_META]: augmentMetaToServer(action)
      };
    default:
      return {};
  }
}

export function channelsToLocal(resChannles: any): ActionChannel[] {
  let channels: ActionChannel[] = [];

  if (!resChannles || !resChannles.length) {
    channels = DefaultResponseChannels;
  } else {

    for (const resChannel of (resChannles || [])) {
      channels.push({
        actions: resChannel.actions && resChannel.actions.map(actionToLocal),
        tag: resChannel.tag
      });
    }
  }

  return channels;
}

export function channelsToServer(channels: ActionChannel[]) {
  return (channels || []).map((channel: ActionChannel) => ({
    tag: channel.tag,
    actions: (channel.actions || []).map(actionToServer)
  }));
}

export function responseToLocal(response: any): ResponseConfig {
  const responsePayload: ResponseConfig = {
    ...basePageToLocal(response),
    isElseBranch: response.is_else_branch,
    conditionType: response.condition_type || SourceType.CODE_EXPRESSION,
    triggerConditions: (response.trigger_conditions || []).map(conditionToLocal),
    codeCondition: response.code_condition,
    codeComment: response.code_comment,
    needConfirmation: response.need_confirmation,
    confirmQuestions: response.confirm_questions || [],
    confirmExpressionsYes: response.confirm_expressions_yes &&
      response.confirm_expressions_yes.map(trainingPhraseToLocal) || [],
    confirmExpressionsNo: response.confirm_expressions_no &&
      response.confirm_expressions_no.map(trainingPhraseToLocal) || [],
    channels: channelsToLocal(response.channels),
    masterSelf: response.master_self && responseToLocal(response.master_self)
  };

  return responsePayload;
}

export function responseToServer(response: ResponseConfig) {

  const responsePayload: any = {
    id: response.id,
    name: response.name || '',
    condition_type: response.conditionType,
    trigger_conditions: response.triggerConditions.map(conditionToServer),
    code_condition: response.codeCondition,
    code_comment: response.codeComment,
    need_confirmation: response.needConfirmation,
    confirm_questions: response.confirmQuestions,
    channels: channelsToServer(response.channels)
  };

  return responsePayload;
}

export const DefaultResponseChannels: ActionChannel[] = [
  {
    tag: RESPONSE_CHANNEL_TYPE.RESTFUL,
    actions: [
    ]
  }
];

export const DefaultElseBranch = {
  name: 'else_branch',
  isElseBranch: true,
  triggerConditions: [],
  channels: DefaultResponseChannels
};

